<script setup>
  import { computed, watch, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { useRouter } from "vue-router";
  import { ExternalLink, Info } from "lucide-vue-next";

  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Progress } from "@/components/ui/progress";

  import { useContractsStore } from "@stores/contracts";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { useSurveyCreditsStore, NO_CONTRACT_VALUE } from "@stores/survey/setup/surveyCredits";
  import { creditsLabel } from "@utils/credits/creditRules";

  const router = useRouter();
  const contractsStore = useContractsStore();
  const surveyStore = useSurveyStore();
  const currentUserStore = useCurrentUserStore();
  const creditsStore = useSurveyCreditsStore();

  const { survey } = storeToRefs(surveyStore);
  const { isAdmin, isSale, isClient } = storeToRefs(currentUserStore);
  const { currentContractId, contractChoicePending } = storeToRefs(creditsStore);
  const { updateContractId } = creditsStore;

  const canViewContracts = computed(() => isAdmin.value || isSale.value);

  const goToContractsPage = () => {
    if (!canViewContracts.value || !selectedContract.value) return;
    // Pre-fill the search bar by setting the store directly; the destination
    // page reads the same ref on mount.
    contractsStore.searchQuery = selectedContract.value.name;
    router.push("/contracts");
  };

  const loaded = ref(false);
  const accountContracts = ref([]);

  const canSelectNoContract = computed(() => isAdmin.value || isSale.value);
  // startFromDefault copies the template's id into the survey object and only
  // clears token/chat_token, so we can't rely on survey.id to know whether
  // we're creating vs editing. token is null at creation, set after save.
  const isPersisted = computed(() => Boolean(survey.value?.token));
  const isLocked = computed(() => isPersisted.value && isClient.value);

  const contractOptions = computed(() => {
    const options = accountContracts.value.map((c) => ({
      label: `${c.name} (${c.credits_remaining} ${creditsLabel(c.credits_remaining)} remaining)`,
      value: String(c.id),
    }));
    if (canSelectNoContract.value) {
      options.unshift({ label: "No contract (admin only)", value: NO_CONTRACT_VALUE });
    }
    return options;
  });

  const selectedValue = computed(() => {
    // After an account switch the admin must choose explicitly — show the
    // placeholder, not the "No contract" default.
    if (contractChoicePending.value) return null;
    if (currentContractId.value === null && canSelectNoContract.value) return NO_CONTRACT_VALUE;
    return currentContractId.value ? String(currentContractId.value) : null;
  });

  const selectedContract = computed(() =>
    accountContracts.value.find((c) => c.id === currentContractId.value),
  );

  const consumedPercentage = computed(() => {
    if (!selectedContract.value || selectedContract.value.total_credits === 0) return 0;
    const consumed = selectedContract.value.credits_consumed_total;
    const total = selectedContract.value.total_credits;
    return Math.min(100, Math.round((consumed / total) * 100));
  });

  // Refetch when the survey's account changes (e.g. user switches account in General
  // section). The account switch itself clears the stale contract in
  // surveyAccount.updateAccountId, so a null contract_id here means "pick a default".
  watch(
    () => survey.value?.account_id,
    async (accountId) => {
      loaded.value = false;
      accountContracts.value = await contractsStore.fetchActiveForAccount(accountId);
      loaded.value = true;
      if (isLocked.value) return;
      // Admin just switched account → let them pick a contract; don't auto-default.
      if (contractChoicePending.value) return;
      if (currentContractId.value !== null) return;

      if (canSelectNoContract.value) {
        // Admin/Sales/SuperAdmin → default to "No contract" so they pick
        // a contract consciously if they want one.
        updateContractId(NO_CONTRACT_VALUE);
      } else if (accountContracts.value.length > 0) {
        // Clients → auto-pick the contract with the closest expiration date
        // (the one to consume first).
        const soonest = [...accountContracts.value].sort(
          (a, b) => new Date(a.ends_at) - new Date(b.ends_at),
        )[0];
        updateContractId(String(soonest.id));
      }
    },
    { immediate: true },
  );
</script>

<template>
  <div class="w-1/2 space-y-2">
    <Label>Contract</Label>

    <Select
      v-if="loaded"
      :model-value="selectedValue"
      :options="contractOptions"
      :disabled="isLocked"
      placeholder="Select a contract"
      class="w-full"
      @update:model-value="updateContractId" />

    <p
      v-if="!loaded"
      class="text-xs text-muted-foreground">
      Loading contracts...
    </p>

    <p
      v-if="loaded && !isLocked && !accountContracts.length && !canSelectNoContract"
      class="text-xs text-status-warning-strong">
      No active contract found for this account. Contact your CSM to set one up.
    </p>

    <p
      v-if="isLocked"
      class="text-xs text-muted-foreground">
      The contract can't be changed once the survey is created. Contact your CSM to request a change.
    </p>

    <div
      v-if="selectedContract"
      class="rounded-lg border border-border bg-muted/30 px-4 py-3 mt-3 space-y-2"
      :class="{ 'cursor-pointer hover:bg-muted/60 transition-colors': canViewContracts }"
      @click="goToContractsPage">
      <div class="flex items-center justify-between gap-2">
        <p class="text-sm font-semibold truncate">{{ selectedContract.name }}</p>
        <ExternalLink
          v-if="canViewContracts"
          class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </div>
      <div class="flex items-center gap-3">
        <Progress
          :model-value="consumedPercentage"
          class="h-2 flex-1 [&>div]:bg-status-success" />
        <span class="text-xs text-muted-foreground whitespace-nowrap">
          {{ selectedContract.credits_remaining }} / {{ selectedContract.total_credits }} credit(s) remaining
        </span>
      </div>
    </div>

    <div
      v-else-if="loaded && !isLocked && canSelectNoContract && currentContractId === null && !contractChoicePending"
      class="rounded-lg border border-border bg-muted/30 px-4 py-3 mt-3 text-xs text-muted-foreground flex items-start gap-2">
      <Info class="h-4 w-4 flex-shrink-0 mt-0.5" />
      <p>No contract associated. This survey will not consume any contract credits.</p>
    </div>
  </div>
</template>
